/**
 * Shared application constants used by both the client and the server.
 * Keep this file free of any runtime/IO dependencies.
 */

export const APP_NAME = 'Flower Blend Catalog';

/** Supported image MIME types for uploads. */
export const SUPPORTED_IMAGE_MIME_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
] as const;

export type SupportedImageMimeType = (typeof SUPPORTED_IMAGE_MIME_TYPES)[number];

/** Categories an uploaded image can be tagged as. */
export const IMAGE_CATEGORIES = [
  'package_front',
  'potency_label',
  'lot_date_label',
  'product_name',
  'additional_details',
  'product_info',
  'sticky_note',
  'other',
] as const;

export type ImageCategory = (typeof IMAGE_CATEGORIES)[number];

export const IMAGE_CATEGORY_LABELS: Record<ImageCategory, string> = {
  package_front: 'Package front',
  potency_label: 'Potency label',
  lot_date_label: 'Lot and date label',
  product_name: 'Product name',
  additional_details: 'Additional details',
  product_info: 'Product information',
  sticky_note: 'Sticky note',
  other: 'Other',
};

/** Per-image OCR / capture lifecycle (client draft + optional DB persistence). */
export const IMAGE_OCR_STATUSES = [
  'none',
  'captured',
  'crop_needed',
  'ready_to_scan',
  'scanning',
  'review_needed',
  'confirmed',
  'error',
  'skipped',
] as const;

export type ImageOcrStatus = (typeof IMAGE_OCR_STATUSES)[number];

export const IMAGE_OCR_STATUS_LABELS: Record<ImageOcrStatus, string> = {
  none: 'Not started',
  captured: 'Photo captured',
  crop_needed: 'Crop needed',
  ready_to_scan: 'Ready to scan',
  scanning: 'Scanning',
  review_needed: 'Review needed',
  confirmed: 'Confirmed',
  error: 'Error',
  skipped: 'Skipped',
};

/** How a strain record was entered. */
export const SOURCE_TYPES = [
  'manual',
  'local_manual',
  'package_photo',
  'sticky_note',
  'imported',
  'spreadsheet',
] as const;

export type SourceType = (typeof SOURCE_TYPES)[number];

/** Catalogue ownership for Library badges / filters. */
export type CatalogueSourceKind = 'spreadsheet' | 'local';

export function catalogueSourceKind(
  sourceType: SourceType | string,
  productId: string | null | undefined,
): CatalogueSourceKind {
  // One-time catalogue bootstrap into device-local storage is not live sync.
  if (sourceType === 'imported') {
    return 'local';
  }
  if (sourceType === 'spreadsheet' || (productId != null && productId !== '')) {
    return 'spreadsheet';
  }
  return 'local';
}

/** Common product categories (free-form is also allowed). */
export const COMMON_CATEGORIES = [
  'Flower',
  'Pre-roll',
  'Ground',
  'Shake',
  'Concentrate',
  'Other',
] as const;

/** Calculation modes for the blend optimizer. */
export const CALCULATION_MODES = [
  'closest',
  'do_not_exceed',
  'at_least',
  'target_range',
] as const;

export type CalculationMode = (typeof CALCULATION_MODES)[number];

export const CALCULATION_MODE_LABELS: Record<CalculationMode, string> = {
  closest: 'Closest to target',
  do_not_exceed: 'Do not exceed target',
  at_least: 'At least target',
  target_range: 'Target range',
};

/** Field-level limits (mirrored in Zod schemas). */
export const LIMITS = {
  NAME_MAX: 200,
  PRODUCER_MAX: 200,
  LOT_MAX: 120,
  NOTES_MAX: 5000,
  OCR_TEXT_MAX: 20000,
  THC_MIN: 0,
  THC_MAX: 100,
  CBD_MIN: 0,
  CBD_MAX: 100,
  TERPENES_MIN: 0,
  TERPENES_MAX: 100,
} as const;

/** Defaults for the blend calculator (overridable via settings). */
export const CALCULATOR_DEFAULTS = {
  totalWeightGrams: 0.25,
  targetThcMg: 65,
  scalePrecisionGrams: 0.01,
  strainCount: 2,
} as const;

/** Precision options the UI offers for scales. */
export const SCALE_PRECISION_OPTIONS = [0.1, 0.01, 0.001] as const;

/** Conversion factor: 1% THC by weight = 10 mg THC per gram. */
export const PERCENT_TO_MG_PER_GRAM = 10;

/** Safety notice shown near calculation results. */
export const CALCULATION_DISCLAIMER =
  'Theoretical estimate based on entered or label-reported potency. Actual product ' +
  'potency, combustion losses, absorption, and effects can vary. Verify OCR-derived ' +
  'values before calculating. This is not an estimate of absorbed THC, bioavailability, ' +
  'impairment, or individual effect.';

/** OCR region / intent selected in the crop UI. */
export const OCR_SCAN_MODES = ['full', 'potency', 'product-name'] as const;
export type OcrScanMode = (typeof OCR_SCAN_MODES)[number];

export const OCR_SCAN_MODE_LABELS: Record<OcrScanMode, string> = {
  full: 'Full image',
  potency: 'Potency label',
  'product-name': 'Product name',
};

/** OCR speed/depth strategy (Phase A: shapes the request; staged variants later). */
export const OCR_SCAN_STRATEGIES = ['standard', 'quick'] as const;
export type OcrScanStrategy = (typeof OCR_SCAN_STRATEGIES)[number];

/** Version of the persisted crop-metadata JSON shape. */
export const CROP_METADATA_SCHEMA_VERSION = 1 as const;

/**
 * Staged OCR: how many preprocessing variants to try first for quick scan.
 * Full comparison/debug may run all; thresholds are env-configurable later.
 */
export const OCR_STAGED_DEFAULTS = {
  quickInitialVariants: 2,
  /** 0–100 engine confidence below which extra variants may run (Phase B+). */
  escalateBelowConfidence: 55,
} as const;
